/**
 * Admin: Search Index Health
 *
 * Wraps the read-only `diagnoseSyncStatus` endpoint and shows missing /
 * orphaned / no-DB-id breakdowns plus recommended remediation actions.
 * See ADMIN_PORTAL_UX_REQUIREMENTS.md §3.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowRight } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { diagnoseSyncStatus, type DiagnoseSyncResponse } from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  StatGrid,
  Metric,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  SampleTable,
  RawJson,
  ButtonRow,
} from '../components/admin/AdminUI';
import { formatEventLocalDateTime } from '../utils/eventDateTime';

export const AdminSearchHealthPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DiagnoseSyncResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    setStartedAt(Date.now());
    try {
      const r = await diagnoseSyncStatus();
      setData(r);
      setCheckedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to run health check');
    } finally {
      setLoading(false);
      setStartedAt(null);
    }
  };

  // Initial run on mount
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const goSyncAddOnly = () => navigate('/admin/sync-index?skipCleanup=1');
  const goSyncFull = () => navigate('/admin/sync-index');
  const goCleanup = () => navigate('/admin/cleanup-orphans');

  const recAction = (action: string) => {
    switch (action) {
      case 'add_missing':    return { label: 'Add missing events',  onClick: goSyncAddOnly };
      case 'remove_orphans': return { label: 'Remove orphans',      onClick: goCleanup };
      case 'full_sync':      return { label: 'Full sync',           onClick: goSyncFull };
      default: return null;
    }
  };

  return (
    <AdminLayout
      currentPage="searchHealth"
      pageTitle="Search Index Health"
      pageDescription="Compare every event in the database against every search index document."
    >
      <Card>
        <CardTitle>Run health check</CardTitle>
        <CardDescription>
          This scan is expensive — it enumerates the entire index. Run it
          manually rather than on a schedule.
        </CardDescription>
        <ButtonRow>
          <PrimaryButton onClick={run} disabled={loading}>
            <RefreshCw size={16} />
            {loading ? 'Running…' : 'Re-run check'}
          </PrimaryButton>
          {checkedAt && !loading && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              Checked at {checkedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>
        {loading && startedAt != null && (
          <RunningIndicator startedAt={startedAt} message="Scanning database and index…" />
        )}
        {error && <Banner tone="error">{error}</Banner>}
      </Card>

      {data && (
        <>
          <Banner tone={data.overview.inSync ? 'success' : 'warning'}>
            {data.overview.inSync
              ? '✓ Database and search index are in sync.'
              : `⚠ Out of sync — ${data.missingFromSearch.count} events missing, ${data.orphanedInSearch.count} orphaned.`}
          </Banner>

          <StatGrid>
            <Metric label="DB events" value={data.overview.databaseEventCount.toLocaleString()} />
            <Metric label="Search docs" value={data.overview.searchDocumentCount.toLocaleString()} />
            <Metric
              label="Δ"
              value={data.overview.difference.toLocaleString()}
              tone={data.overview.difference === 0 ? 'success' : 'warning'}
            />
          </StatGrid>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <Card>
              <CardTitle>
                {data.missingFromSearch.count.toLocaleString()} events not yet indexed
              </CardTitle>
              {data.missingFromSearch.sampleEvents && data.missingFromSearch.sampleEvents.length > 0 && (
                <SampleTable>
                  <thead>
                    <tr>
                      <th>Id</th>
                      <th>Title</th>
                      <th>Start</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.missingFromSearch.sampleEvents.slice(0, 10).map((e) => (
                      <tr key={e.id}>
                        <td>{e.id}</td>
                        <td>{e.title ?? '—'}</td>
                        <td>{formatEventLocalDateTime(e.startDate) || '—'}</td>
                        <td>{e.dateCreated ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              )}
            </Card>

            <Card>
              <CardTitle>
                {data.orphanedInSearch.count.toLocaleString()} search docs reference deleted events
              </CardTitle>
              {data.orphanedInSearch.sampleDocs && data.orphanedInSearch.sampleDocs.length > 0 && (
                <SampleTable>
                  <thead>
                    <tr>
                      <th>SearchId</th>
                      <th>DB Id</th>
                      <th>Title</th>
                      <th>Start</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orphanedInSearch.sampleDocs.slice(0, 10).map((d) => (
                      <tr key={d.searchId}>
                        <td>{d.searchId}</td>
                        <td>{d.databaseEventId ?? '—'}</td>
                        <td>{d.title ?? '—'}</td>
                        <td>{formatEventLocalDateTime(d.startDate) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              )}
            </Card>

            <Card>
              <CardTitle>
                {data.documentsWithoutDbId.count.toLocaleString()} legacy docs (no databaseEventId)
              </CardTitle>
              <CardDescription>
                {data.documentsWithoutDbId.note ??
                  'These documents may predate databaseEventId tracking. They cannot be matched to a DB row.'}
              </CardDescription>
            </Card>
          </div>

          {data.missingFromSearch.byCreationDate &&
            Object.keys(data.missingFromSearch.byCreationDate).length > 0 && (
              <Card>
                <CardTitle>Missing by creation date</CardTitle>
                <CardDescription>
                  Spikes here often indicate the day the sync broke.
                </CardDescription>
                <ByDateBars data={data.missingFromSearch.byCreationDate} />
              </Card>
            )}

          {data.recommendations && data.recommendations.length > 0 && (
            <Card>
              <CardTitle>Recommended actions</CardTitle>
              {data.recommendations.map((rec, i) => {
                const a = recAction(rec.action);
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: i < data.recommendations.length - 1 ? '1px solid #f0f0f0' : 'none',
                      gap: 12,
                    }}
                  >
                    <div style={{ fontSize: 14 }}>{rec.message}</div>
                    {a && (
                      <SecondaryButton onClick={a.onClick}>
                        {a.label}
                        <ArrowRight size={16} />
                      </SecondaryButton>
                    )}
                  </div>
                );
              })}
            </Card>
          )}

          <RawJson data={data} />
        </>
      )}
    </AdminLayout>
  );
};

const ByDateBars: React.FC<{ data: Record<string, number> }> = ({ data }) => {
  const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
      {entries.map(([date, count]) => (
        <div key={date} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 50px', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <div style={{ fontFamily: 'monospace' }}>{date}</div>
          <div style={{ background: '#f0f0f0', borderRadius: 4, height: 12 }}>
            <div style={{ width: `${(count / max) * 100}%`, background: '#faad14', height: '100%', borderRadius: 4 }} />
          </div>
          <div style={{ textAlign: 'right', color: '#595959' }}>{count}</div>
        </div>
      ))}
    </div>
  );
};

export default AdminSearchHealthPage;
